import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  Header,
  Message,
  TypingIndicator,
  Avatar,
  InputContainer,
  HistoryLoader,
  FeedbackPanel,
  ConnectionError
} from './components';
import SuggestionTicker from './components/SuggestionTicker';
import NextSteps from './components/NextSteps';
import WelcomeHero from './components/WelcomeHero';
import ChatEndedBar from './components/ChatEndedBar';
import ChatDivider from './components/ChatDivider';
import ConfirmDialog from './components/ConfirmDialog';
import ContactPicker from './components/ContactPicker';
import {
  selectMessages,
  selectInputText,
  selectIsTyping,
  selectIsStreaming,
  selectSessionId,
  selectIsInitializingSession,
  selectClientData,
  selectIsLoadingHistory,
  selectIsClosingSession,
  selectSessionError,
  selectRetryCount,
  selectMessageQueue,
  selectIsProcessingQueue,
  selectHandoff,
  fetchAgentUpdates,
  AGENT_POLL_INTERVAL_MS,
  setInputText,
  sendUserMessage,
  streamBotResponse,
  initializeSession,
  setClientData,
  closeSession,
  clearSessionError,
  incrementRetryCount,
  enqueueMessage,
  processMessageQueue,
  sendAttachments
} from './store/chatSlice';
import { attachmentProblem, MAX_FILES_PER_SEND } from './utils/attachments';

const ChatContainer = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  /* A shade darker than #f1f3f6 so white bubbles stand clear of it in light and flipped dark mode */
  background: #ebeef3;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
`;

const TypingMessage = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const AgentTypingLabel = styled.span`
  align-self: center;
  font-size: 12px;
  color: #64748b;
`;

const QueueIndicator = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  background: #e7edf3;
  border: 1px solid #275386;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: #275386;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

// Hints cycled in the empty input box. They feature what clients rarely use (Aura usage, Jul-Oct 2026);
// case status, ETA and attention-required reasons are already well known, so they are left out.
// Keep each short: the widget is ~400px wide and long text gets clipped.
const PLACEHOLDER_HINTS = [
  'Try: "Send my report on WhatsApp"',
  'Try: "Raise a rework on my report"',
  'Try: "Connect me to the radiologist"',
  'Try: "Download my invoice"',
  'Try: "Summarise my report findings"',
  'Try: "Update my case history"',
  'Try: "How do I upload images?"',
  'Try: "Show my draft cases"',
];
const PLACEHOLDER_INTERVAL_MS = 4000;

const ChatBot = () => {
  const dispatch = useDispatch();
  const messages = useSelector(selectMessages);
  const inputText = useSelector(selectInputText);
  const isTyping = useSelector(selectIsTyping);
  const isStreaming = useSelector(selectIsStreaming);
  const sessionId = useSelector(selectSessionId);
  const isInitializingSession = useSelector(selectIsInitializingSession);
  const clientData = useSelector(selectClientData);
  const isLoadingHistory = useSelector(selectIsLoadingHistory);
  const isClosingSession = useSelector(selectIsClosingSession);
  const sessionError = useSelector(selectSessionError);
  const retryCount = useSelector(selectRetryCount);
  const messageQueue = useSelector(selectMessageQueue);
  const isProcessingQueue = useSelector(selectIsProcessingQueue);
  const handoff = useSelector(selectHandoff);
  const [confirmLeave, setConfirmLeave] = useState(null);  // 'newChat' | 'close' while asking
  // Live chat: files chosen but not sent yet (the typed text goes with them as a caption)
  const [stagedFiles, setStagedFiles] = useState([]);
  const [attachError, setAttachError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null); // Add ref for input field
  const [wasStreaming, setWasStreaming] = useState(false); // Track previous streaming state
  const [flag, setFlag] = useState(0);
  const [caseNotification, setCaseNotification] = useState(null); // Add state for case notification
  
  // Feedback panel state
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);
  const [feedbackActionType, setFeedbackActionType] = useState(''); // 'newSession' or 'close'
  const [pendingCloseAction, setPendingCloseAction] = useState(false); // Track if we need to close chat

  // Track if quick actions should be shown
  const [showQuickActions, setShowQuickActions] = useState(true);

  // Rotating placeholder example; only ticks while the box is empty so it never fights the user
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  useEffect(() => {
    if (inputText) return undefined;
    const id = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % PLACEHOLDER_HINTS.length);
    }, PLACEHOLDER_INTERVAL_MS);
    return () => clearInterval(id);
  }, [inputText]);
  
  // Function to close the chat modal in parent
  const closeChat = () => {
    window.parent.postMessage({ type: 'CLOSE_CHAT_MODAL' }, '*');
  };

  // Function to retry session initialization
  const handleRetryConnection = () => {
    dispatch(incrementRetryCount());
    dispatch(clearSessionError());
    dispatch(initializeSession());
  };

  const messagesContainerRef = useRef(null);
  // Follow new text only while the user is at (or near) the bottom; if they scroll up to read, leave them there
  const stickToBottomRef = useRef(true);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  // Instant, not smooth: a smooth scroll per streamed chunk queued up and lagged behind the text
  const scrollToBottom = () => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // A new message (e.g. the user just sent one) always brings the view down
  useEffect(() => {
    stickToBottomRef.current = true;
    scrollToBottom();
  }, [messages.length, isTyping]);

  // Streamed text grows the bubble between Redux updates (smooth reveal), so follow the DOM itself
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return undefined;
    const observer = new MutationObserver(() => {
      if (stickToBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [isLoadingHistory, isInitializingSession, sessionError]);

  // Auto-focus input when streaming completes and process queue
  useEffect(() => {
    console.log('Queue trigger useEffect:', { 
      wasStreaming, 
      isStreaming, 
      isLoadingHistory, 
      isInitializingSession, 
      queueLength: messageQueue.length, 
      isProcessingQueue 
    });
    
    // If we were streaming and now we're not, focus the input and process queue
    if (wasStreaming && !isStreaming && !isLoadingHistory && !isInitializingSession) {
      console.log('Bot finished streaming, checking queue...');
      
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100); // Small delay to ensure DOM is updated
      
      // Process queue if there are pending messages
      if (messageQueue.length > 0 && !isProcessingQueue) {
        console.log('Starting queue processing with', messageQueue.length, 'messages');
        setTimeout(() => {
          dispatch(processMessageQueue());
        }, 200); // Small delay after focusing input
      } else {
        console.log('No queue processing needed:', { queueLength: messageQueue.length, isProcessingQueue });
      }
    }
    
    // Update the previous streaming state
    setWasStreaming(isStreaming);
  }, [isStreaming, isLoadingHistory, isInitializingSession, wasStreaming, messageQueue.length, isProcessingQueue, dispatch]);

  // Initialize session when component mounts
  useEffect(() => {
    let sessionInitialized = false;
    let postMessageTimeout;

    // Function to initialize session
    const initializeSessionWithData = (clientData = null) => {
      // Only initialize if we haven't tried before and there's no error
      if (!sessionInitialized && !sessionId && !isInitializingSession && !sessionError) {
        sessionInitialized = true;
        console.log('Initializing session with data:', clientData);
        
        // Add a small delay before initializing session
        setTimeout(() => {
          dispatch(initializeSession());
        }, 100);
      }
    };

    // Function to handle incoming messages
    const handleMessage = (event) => {
      // Security check: Verify the origin
      const {REACT_APP_DOMAIN_URLS} = process.env;
      const allowedOrigins = REACT_APP_DOMAIN_URLS?.split(',');
      // console.log('allowedOrigins', allowedOrigins,'event.origin', event.origin);
      console.log('event_data',typeof(event.data),event.data);
      console.log('event.origin', event.origin);
      if (!allowedOrigins?.includes(event.origin)) {
      // if(event.origin) {
        console.log('event.origin', event.origin);
        console.warn('Received message from unauthorized origin:', event.origin);
        return;
      }
      // postMessage can send either a string or an object; normalize to object
      // Ignore non-JSON strings (e.g. webpack's "webpackHotUpdate…" messages in dev) instead of crashing
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (!data || typeof data !== 'object') return;
      // Check if it's the message we're expecting
      if (data.type == 'CLIENT_DATA') {
        // console.log('event.data', event.data);
        const { clientId, email, caseNotification } = data;
        
        // console.log('Received Client Data:',clientId, email, caseNotification);
        
        // Clear the timeout since we received the message
        if (postMessageTimeout) {
          clearTimeout(postMessageTimeout);
        }
        
        // Update Redux store with received data
        dispatch(setClientData({
          clientId: clientId,
          email: email
        }));
        
        // Store case notification if present
        if (caseNotification) {
          setCaseNotification(caseNotification);
        }
        
        // Store in localStorage if needed
        // localStorage.setItem('clientId', clientId);
        // localStorage.setItem('email', email);

        // Send confirmation back to parent (optional)
       
        event.source.postMessage({
          type: 'CLIENT_DATA_RECEIVED',
          status: 'success',
          receivedData: { clientId, email, caseNotification }
        }, event.origin);
      

        // Initialize authenticated session with received data

        initializeSessionWithData({ clientId, email });
      }

      if (data.type == 'CLIENT_MOBILE_DATA') {
         const clientId = data.client_id;
         const email = data.user_email;
         const caseNotification = data.caseNotification;
        console.log('Received Client Data:',clientId, email, caseNotification);
        
        // Clear the timeout since we received the message
        if (postMessageTimeout) {
          clearTimeout(postMessageTimeout);
        }
        
        // Update Redux store with received data
        dispatch(setClientData({
          clientId: clientId,
          email: email
        }));
        
        // Store case notification if present
        if (caseNotification) {
          setCaseNotification(caseNotification);
        }
        
        // Store in localStorage if needed
        // localStorage.setItem('clientId', clientId);
        // localStorage.setItem('email', email);

        // Send confirmation back to parent (optional)
       
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'CLIENT_DATA_RECEIVED',
          status: 'success',
          receivedData: { clientId, email, caseNotification }
        }));
      

        // Initialize authenticated session with received data

        initializeSessionWithData({ clientId, email });
      }
    };

    // Set timeout to initialize unauthenticated session if no post message is received
    postMessageTimeout = setTimeout(() => {
      // console.log('No post message received from parent, initializing unauthenticated session');
      
      // Initialize unauthenticated session
      initializeSessionWithData(null);
    }, 3000); // Wait 3 seconds for post message

    // Add event listener
    window.addEventListener('message', handleMessage);

    // Cleanup function
    return () => {
      window.removeEventListener('message', handleMessage);
      if (postMessageTimeout) {
        clearTimeout(postMessageTimeout);
      }
    };
  }, [dispatch]); // Remove sessionId and isInitializingSession from dependencies to prevent auto-retry

  // Handle automatic message sending for AR cases after session is ready
  useEffect(() => {
    // Check if session is ready, history is loaded, and we have case notification
    // Wait until we know this isn't a resumed live chat: the AR request is for Aura, not the agent
    // (it stays pending and is sent in the next bot chat)
    const botChat = handoff.checked && handoff.mode === 'BOT' && !handoff.locked;
    if (sessionId && !isLoadingHistory && !isInitializingSession && caseNotification && botChat) {
      const { type, orderId } = caseNotification;
      
      // Check if it's AR type and has order IDs (greater than 1)
      if (type === 'AR' && orderId && Array.isArray(orderId) && orderId.length >= 1) {
        // Format the message
        const orderList = orderId.map((id, index) => `${index + 1}. ${id}`).join('\n');
        const message = `Help me with the below AR cases with reason:\n${orderList}`;
        
        // Check if bot is currently streaming or processing queue
        if (isStreaming || isProcessingQueue) {
          // Add to queue with high priority since it's an AR case
          console.log('Bot is busy, adding AR case to queue with high priority');
          dispatch(enqueueMessage({ 
            message, 
            priority: 'high' 
          }));
        } else {
          // Send immediately
          setTimeout(() => {
            handleSendMessage(message);
          }, 500);
        }
        
        // Clear the case notification after handling to prevent re-processing
        setCaseNotification(null);
      }
    }
  }, [sessionId, isLoadingHistory, isInitializingSession, caseNotification, isStreaming, isProcessingQueue, handoff.checked, handoff.mode, handoff.locked, dispatch]);

  // Live chat with a 5C agent: check once per session (a reopened chat may still be live),
  // then poll while the backend says a live chat is open
  useEffect(() => {
    if (!sessionId || isInitializingSession) return undefined;
    dispatch(fetchAgentUpdates());
    if (!handoff.poll) return undefined;
    const timer = setInterval(() => dispatch(fetchAgentUpdates()), AGENT_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [sessionId, isInitializingSession, handoff.poll, dispatch]);

  // Focus input after history loads
  useEffect(() => {
    if (!isLoadingHistory && !isInitializingSession && sessionId) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200); // Small delay after history loads
    }
  }, [isLoadingHistory, isInitializingSession, sessionId]);

  // source tells the backend where the message came from ('typed', 'chip', 'next_step') so chip and button taps are tracked
  const handleSendMessage = async (text = inputText, source = 'typed') => {
    // console.log('clientData', clientData);
    if (!text.trim() || !sessionId || isInitializingSession) return;

    // If bot is currently streaming or processing queue, add to queue
    if (isStreaming || isProcessingQueue) {
      console.log('Bot is busy, adding message to queue:', text);
      dispatch(enqueueMessage({ 
        message: text, 
        priority: 'normal',
        source
      }));
      // Clear input only if it's the current input text
      if (text === inputText) {
        dispatch(setInputText(''));
      }
      return;
    }

    // Send user message to Redux store
    dispatch(sendUserMessage(text));
    
    // Trigger streaming bot response
    dispatch(streamBotResponse({ text, source }));
  };

  // Files chosen in the composer: kept above the box until sent; bad ones are named, not kept
  const handleAddFiles = (files) => {
    const room = MAX_FILES_PER_SEND - stagedFiles.length;
    const problems = [];
    const accepted = [];
    files.forEach((file) => {
      const problem = attachmentProblem(file);
      if (problem) problems.push(`${file.name}: ${problem}`);
      else if (accepted.length < room) accepted.push(file);
      else problems.push(`${file.name}: up to ${MAX_FILES_PER_SEND} files at a time.`);
    });
    if (accepted.length) setStagedFiles((current) => [...current, ...accepted]);
    setAttachError(problems.length ? problems.join(' ') : null);
    inputRef.current?.focus();
  };

  const handleRemoveFile = (index) => {
    setStagedFiles((current) => current.filter((_, i) => i !== index));
    setAttachError(null);
  };

  // Files first, one message each, then the text as their caption - the order Nexus uses
  const handleSend = async () => {
    if (!inLiveChat || stagedFiles.length === 0) {
      handleSendMessage();
      return;
    }
    const files = stagedFiles;
    const caption = inputText.trim();
    setStagedFiles([]);
    setAttachError(null);
    dispatch(setInputText(''));
    await dispatch(sendAttachments({ files }));
    if (caption) handleSendMessage(caption);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    dispatch(setInputText(e.target.value));
  };

  const getInputPlaceholder = () => {
    // if (isInitializingSession) {
    //   return "Initializing session...";
    // }
    if (inLiveChat) {
      if (stagedFiles.length) return 'Add a caption (optional)...';
      return isStreaming ? "Sending..." : `Message ${handoff.agentName || '5C Support'}...`;
    }
    if (isStreaming) {
      return "AI is responding...";
    }
    if (isProcessingQueue) {
      return "Processing queued messages...";
    }
    // return "Type or speak your message...";
    return PLACEHOLDER_HINTS[placeholderIndex];
  };

  const isInputDisabled = isStreaming || isInitializingSession || !sessionId || isProcessingQueue;
  // A person has (or is about to take) this chat: bot-only UI is hidden
  const inLiveChat = handoff.mode !== 'BOT' && !handoff.locked;
  const showBotShortcuts = !inLiveChat && !handoff.locked;

  const lastMessage = messages[messages.length - 1];

  // Runs of messages from the same person are grouped; a status line or divider ends a run
  const speakerOf = (message) => {
    if (!message || message.sender === 'system') return null;
    if (message.isUser) return 'user';
    return message.sender === 'agent' ? `agent:${message.senderName || ''}` : 'bot';
  };

  // Files arrive one message each (from the client's tray, or an agent's several attachments in
  // Nexus); a run of them from one person is drawn as a single row of tiles
  const isFileOnly = (message) => Boolean(message.attachment) && !(message.text || '').trim();
  const visibleMessages = messages
    .filter((message) => !message.isWelcome)
    .reduce((items, message) => {
      const previous = items[items.length - 1];
      const tile = { key: message.id, attachment: message.attachment, uploading: message.uploading, uploadError: message.uploadError };
      if (isFileOnly(message) && previous?.attachments && speakerOf(previous) === speakerOf(message)) {
        items[items.length - 1] = {
          ...previous,
          attachments: [...previous.attachments, tile],
          sentToAgent: previous.sentToAgent && message.sentToAgent,
          lastId: message.id,
        };
      } else if (isFileOnly(message)) {
        items.push({ ...message, attachments: [tile], lastId: message.id });
      } else {
        items.push(message);
      }
      return items;
    }, []);

  // Waiting files belong to the live chat; drop them if it ends
  useEffect(() => {
    if (!inLiveChat) {
      setStagedFiles([]);
      setAttachError(null);
    }
  }, [inLiveChat]);
  // "Sent ✓" only under the client's latest message
  const lastUserMessageId = [...visibleMessages].reverse().find((message) => message.isUser)?.id;

  const handleQuickActionSelect = (selectedAction, source = 'chip') => {
    // Hide quick actions panel immediately when user selects an option
    setShowQuickActions(false);
    
    // Send the selected action as a message
    handleSendMessage(selectedAction, source);
  };

  const handleNextStepSelect = (step) => handleQuickActionSelect(step, 'next_step');

  // Leave a live chat or an ended one for a fresh session, without the feedback step
  const handleStartNewChat = () => {
    if (!sessionId || isClosingSession) return;
    dispatch(closeSession({ sessionId, feedback: '', shouldCreateNew: true }));
  };

  // Leaving a live chat ends it (the agent calls back instead), so it is confirmed first
  const handleConfirmLeave = () => {
    const action = confirmLeave;
    setConfirmLeave(null);
    if (action === 'newChat') {
      handleStartNewChat();
    } else if (action === 'close') {
      setFeedbackActionType('close');
      setShowFeedbackPanel(true);
    }
  };
  const handleCancelLeave = useCallback(() => setConfirmLeave(null), []);

  const handleNewSession = () => {
    setFeedbackActionType('newSession');
    setShowFeedbackPanel(true);
  };

  const handleClose = () => {
    if (inLiveChat) {
      setConfirmLeave('close');  // asked in the widget; continues in handleConfirmLeave
      return;
    }
    setFeedbackActionType('close');
    setShowFeedbackPanel(true);
  };

  const handleFeedbackSubmit = async (feedback) => {
    if (sessionId) {
      const shouldCreateNew = feedbackActionType === 'newSession';
      const isCloseAction = feedbackActionType === 'close';
      
      // Set pending close action if this is a close (not new session)
      if (isCloseAction) {
        setPendingCloseAction(true);
      }
      
      try {
        // Dispatch the close session action and wait for it to complete
        const result = await dispatch(closeSession({ 
          sessionId, 
          feedback, 
          shouldCreateNew 
        })).unwrap();
        
        // console.log('Session close operation completed:', result);
        
        if (shouldCreateNew && result.success) {
          // console.log('New session flow completed successfully');
          // New session is already created by the closeSession action
          // Focus input after new session is ready
          setTimeout(() => {
            inputRef.current?.focus();
          }, 300);
        }
        
      } catch (error) {
        console.error('Error during session close/create:', error);
        // Could show error message to user
      }
    }
    setShowFeedbackPanel(false);
  };

  const handleFeedbackClose = () => {
    setShowFeedbackPanel(false);
  };

  // Watch for session close completion to call closeChat
  useEffect(() => {
    // If we were closing session and now we're not, and it was a close action
    if (pendingCloseAction && !isClosingSession) {
      setPendingCloseAction(false);
      // Small delay to ensure UI updates are complete
      setTimeout(() => {
        closeChat();
      }, 500);
    }
  }, [isClosingSession, pendingCloseAction]);

  // Check if chat is fresh (no messages or only welcome message)
  const isFreshChat = messages.length === 0 || 
    (messages.length === 1 && messages[0].text.includes("Hi, I'm Aura"));

  // Hide quick actions when user sends first message
  useEffect(() => {
    if (!isFreshChat && showQuickActions) {
      setShowQuickActions(false);
    }
    // Remove the automatic showing when fresh - let user control it manually
    // else if (isFreshChat && !showQuickActions) {
    //   setShowQuickActions(true);
    // }
  }, [isFreshChat, showQuickActions]);

  return (
    <ChatContainer>
      {/* Queue Indicator */}
      {messageQueue.length > 0 && (
        <QueueIndicator>
          {messageQueue.length} message{messageQueue.length > 1 ? 's' : ''} queued
          {isProcessingQueue && ' (processing...)'}
        </QueueIndicator>
      )}
      
      <Header 
        onNewSession={handleNewSession}
        onClose={handleClose}
        liveChat={inLiveChat ? { mode: handoff.mode, agentName: handoff.agentName } : null}
        onEndChat={() => setConfirmLeave('newChat')}
        onMinimise={closeChat}
      />
      <ConfirmDialog
        open={Boolean(confirmLeave)}
        title={confirmLeave === 'close' ? 'Leave this chat?' : 'End this chat?'}
        message={`This will end your chat with ${handoff.agentName || '5C Support'}. Our team will call you back about this request.`}
        confirmLabel={confirmLeave === 'close' ? 'Leave chat' : 'End chat'}
        cancelLabel="Stay in chat"
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
      
      {/* Show connection error if session initialization failed */}
      {sessionError && !isInitializingSession ? (
        <ConnectionError 
          onRetry={handleRetryConnection}
          isRetrying={isInitializingSession}
          retryCount={retryCount}
        />
      ) : (
        <>
          {/* Scrolling action chips replace the old TopQuickActions panel; a click sends the chip's message */}
          {showBotShortcuts && (
            <SuggestionTicker
              onSelect={handleQuickActionSelect}
              disabled={!sessionId || isInitializingSession || isLoadingHistory || isClosingSession}
            />
          )}

          <MessagesContainer ref={messagesContainerRef} onScroll={handleMessagesScroll}>
            {isLoadingHistory ? (
              <HistoryLoader />
            ) : (
              <>
                {/* A fresh chat shows the mascot instead of a welcome bubble */}
                {isFreshChat && <WelcomeHero />}

                {visibleMessages.map((message, index) => {
                  // A status line naming an agent marks the chat changing hands (connected, transferred)
                  if (message.sender === 'system' && message.senderName) {
                    return <ChatDivider key={message.id} html={message.text} fresh={message.fresh} />;
                  }
                  const speaker = speakerOf(message);
                  return (
                    <Message
                      key={message.id}
                      message={message}
                      isFirstInGroup={!speaker || speakerOf(visibleMessages[index - 1]) !== speaker}
                      isLastInGroup={!speaker || speakerOf(visibleMessages[index + 1]) !== speaker}
                      showDelivery={message.id === lastUserMessageId}
                    />
                  );
                })}

                {/* Aura asked for a name and number: the client's saved contacts, under that reply only */}
                {lastMessage && !lastMessage.isUser && lastMessage.contactPicker && !lastMessage.isStreaming && !handoff.locked && (
                  <ContactPicker
                    sessionId={sessionId}
                    disabled={isStreaming || isClosingSession}
                    onPick={(contact) => handleSendMessage(`${contact.name} - ${contact.phone}`)}
                  />
                )}

                {/* Next steps only under the latest reply, once it has finished streaming */}
                {showBotShortcuts && lastMessage && !lastMessage.isUser && !lastMessage.isStreaming && (
                  <NextSteps
                    items={lastMessage.suggestions}
                    onSelect={handleNextStepSelect}
                    disabled={!sessionId || isStreaming || isClosingSession}
                  />
                )}
                
                {isTyping && (
                  <TypingMessage>
                    <Avatar isUser={false} />
                    <TypingIndicator />
                  </TypingMessage>
                )}

                {handoff.typingAgent && (
                  <TypingMessage aria-live="polite">
                    <Avatar isUser={false} agentName={handoff.typingAgent} />
                    <TypingIndicator />
                    <AgentTypingLabel>{handoff.typingAgent} is typing…</AgentTypingLabel>
                  </TypingMessage>
                )}
              </>
            )}
            
            <div ref={messagesEndRef} />
          </MessagesContainer>
          
          {handoff.locked ? (
            <ChatEndedBar
              key={`${sessionId}-${handoff.csat}`}
              onNewChat={handleStartNewChat}
              busy={isClosingSession || isInitializingSession}
              sessionId={sessionId}
              askRating={handoff.csat}
              agentName={handoff.agentName}
            />
          ) : (
          <InputContainer
            ref={inputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            onSend={handleSend}
            onTranscriptionResult={(text) => dispatch(setInputText(text))}
            clientId={clientData?.clientId ?? '0'}
            onAddFiles={inLiveChat ? handleAddFiles : undefined}
            stagedFiles={inLiveChat ? stagedFiles : []}
            onRemoveFile={handleRemoveFile}
            attachError={inLiveChat ? attachError : null}
            disabled={isInputDisabled || isLoadingHistory || isClosingSession}
            placeholder={
              isLoadingHistory ? "Loading chat history..." : 
              isClosingSession ? "Closing session..." : 
              isProcessingQueue ? "Processing queued messages..." :
              getInputPlaceholder()
            }
          />
          )}
        </>
      )}

      <FeedbackPanel
        isOpen={showFeedbackPanel}
        onClose={handleFeedbackClose}
        onSubmit={handleFeedbackSubmit}
        actionType={feedbackActionType}
        isLoading={isClosingSession}
      />
    </ChatContainer>
  );
};

export default ChatBot; 